// Pure logic behind the Quantity Tiers dialog (client/src/components/quantitytiers/main.vue).
// No Vue, no DOM, no imports beyond the resolver, so Node's built-in test runner executes it
// directly: client/tests/quantityTiersEditor.test.mjs.
//
// Storage is per entity (spec 3.2): component.quantityTiers holds the company coefficient and
// each operation's quantityTiers holds its durations. The dialog shows one panel per threshold -
// the union across the component and its operations - and applying writes a DENSE entry for
// every panel into the component and into every tierable operation.

import { resolveTier } from './quantityTiers.mjs'

export const MAX_THRESHOLD = 2147483647

// At most this many tiers per product - QuantityTiersValidator.MaxTiers (shared fixture "maxTiers").
export const MAX_TIERS = 10

// Operations that carry tiers. The actions table hides duration for category 'Other'
// (ShowIfFormula "item.category != 'Other'" in MetadataRepository), so those have none to override.
export function isTierableAction(action) {
  return !!action && action.category !== 'Other'
}

// The rounding every totals formula uses: (Math.round(1000 * x) / 1000) || 0.
export function roundPrice(value) {
  return (Math.round(1000 * value) / 1000) || 0
}

// null for empty input, otherwise a Number (possibly NaN, which validation reports).
function toNumber(value) {
  return value === null || value === undefined || value === '' ? null : Number(value)
}

// Thresholds in one stored document, as integers. Matches the resolver's key rule (digits
// only, within Int32); zero is dropped because the API rejects it.
function thresholdsOf(tiers) {
  if (!tiers) return []
  return Object.keys(tiers)
    .filter(key => /^\d+$/.test(key))
    .map(Number)
    .filter(threshold => threshold >= 1 && threshold <= MAX_THRESHOLD)
}

// Every distinct threshold across the component and its tierable operations, ascending.
// "0100" and "100" collapse to one threshold.
export function tierThresholds(component) {
  const all = new Set(thresholdsOf(component && component.quantityTiers))
  for (const action of (component && component.actions) || []) {
    if (isTierableAction(action)) thresholdsOf(action.quantityTiers).forEach(threshold => all.add(threshold))
  }
  return [...all].sort((a, b) => a - b)
}

// The real duration a tier starts with when seeded from BASE values. The API fills an unset
// base real duration with the duration itself (Mapper.cs, outbound "?? DurationInMinutes"), so
// "equal to the duration" means "not separately set" - seed null so the tier's own duration
// drives scheduling (ScheduledAction.cs: Real > 0 ? Real : Duration).
function seedRealFromBase(action) {
  const real = toNumber(action.realDurationInMinutes)
  return real !== null && real !== toNumber(action.durationInMinutes) ? real : null
}

function storedDocuments(component) {
  return {
    componentTiers: component.quantityTiers || null,
    actionTiers: (component.actions || []).map(action => (action && action.quantityTiers) || null),
  }
}

// The values in force at `threshold` under `documents`, per entity (spec 3.2): each entity uses
// its own tier that applies at that quantity, else its base value.
function valuesAt(component, documents, threshold) {
  const componentTier = resolveTier(threshold, documents.componentTiers)

  return {
    companyCoeficient: componentTier ? componentTier.companyCoeficient : component.companyCoeficient,
    rows: (component.actions || [])
      .map((action, actionIndex) => ({ action, actionIndex }))
      .filter(({ action }) => isTierableAction(action))
      .map(({ action, actionIndex }) => {
        const tier = resolveTier(threshold, documents.actionTiers[actionIndex])
        return tier
          ? { actionIndex, durationInMinutes: tier.durationInMinutes, realDurationInMinutes: tier.realDurationInMinutes ?? null }
          : { actionIndex, durationInMinutes: action.durationInMinutes, realDurationInMinutes: seedRealFromBase(action) }
      }),
  }
}

function seedSnapshot(values) {
  return JSON.stringify({ companyCoeficient: values.companyCoeficient, rows: values.rows })
}

let lastPanelKey = 0

// `seed` holds the seeded values for panels created in this dialog session, so an untouched
// panel can follow its threshold. Panels loaded from stored documents get null: they are the
// user's values and are never re-seeded.
function newPanel(threshold, values, seeded) {
  return {
    key: ++lastPanelKey,
    threshold,
    companyCoeficient: values.companyCoeficient,
    rows: values.rows,
    seed: seeded ? seedSnapshot(values) : null,
  }
}

// One panel per threshold currently stored on the component, every field filled.
export function buildTierPanels(component) {
  const documents = storedDocuments(component)
  return tierThresholds(component).map(threshold => newPanel(threshold, valuesAt(component, documents, threshold), false))
}

// 100 for the first panel, otherwise ten times the largest threshold, capped at Int32.
export function suggestNextThreshold(panels) {
  if (!panels.length) return 100
  const largest = Math.max(...panels.map(panel => toNumber(panel.threshold) || 0))
  return largest >= 1 ? Math.min(largest * 10, MAX_THRESHOLD) : 100
}

// Adds a panel seeded with the values in force at `threshold` under the dialog's CURRENT
// panels (spec 3.2) - so a new 1000 panel starts from an unsaved 100 panel's edits, not base.
export function addTierPanel(panels, component, threshold = suggestNextThreshold(panels)) {
  const documents = buildTierDocuments(panels, component)
  return [...panels, newPanel(threshold, valuesAt(component, documents, threshold), true)]
}

// True once any value of a session-created panel differs from its seed. Loaded panels count
// as touched: they are never re-seeded.
export function isPanelTouched(panel) {
  return panel.seed === null || seedSnapshot(panel) !== panel.seed
}

// Changes a panel's threshold. An untouched new panel is re-seeded so it keeps meaning "the
// values in force at this quantity"; any other panel keeps the user's values.
export function setPanelThreshold(panels, panelIndex, threshold, component) {
  return panels.map((panel, index) => {
    if (index !== panelIndex) return panel
    if (isPanelTouched(panel)) return { ...panel, threshold }

    const others = panels.filter((_, i) => i !== panelIndex)
    const values = valuesAt(component, buildTierDocuments(others, component), threshold)
    return { ...panel, threshold, companyCoeficient: values.companyCoeficient, rows: values.rows, seed: seedSnapshot(values) }
  })
}

export function removeTierPanel(panels, panelIndex) {
  return panels.filter((_, index) => index !== panelIndex)
}

// Errors as { panelIndex, field, actionIndex?, message }. Messages are English translation
// keys - each needs a row in translations.csv.
export function validateTierPanels(panels) {
  const errors = []
  const used = new Set()

  panels.forEach((panel, panelIndex) => {
    const threshold = panel.threshold
    if (!Number.isInteger(threshold) || threshold < 1 || threshold > MAX_THRESHOLD) {
      errors.push({ panelIndex, field: 'threshold', message: 'Quantity must be a whole number of at least 1' })
    } else if (used.has(threshold)) {
      errors.push({ panelIndex, field: 'threshold', message: 'Another tier already uses this quantity' })
    } else {
      used.add(threshold)
    }

    const coefficient = toNumber(panel.companyCoeficient)
    if (coefficient === null || !Number.isFinite(coefficient) || coefficient < 0) {
      errors.push({ panelIndex, field: 'companyCoeficient', message: 'Company coefficient must be zero or more' })
    }

    panel.rows.forEach(row => {
      const duration = toNumber(row.durationInMinutes)
      if (duration === null || !Number.isFinite(duration) || duration < 0) {
        errors.push({ panelIndex, field: 'durationInMinutes', actionIndex: row.actionIndex, message: 'Duration must be zero or more' })
      }

      const real = toNumber(row.realDurationInMinutes)
      if (real !== null && (!Number.isFinite(real) || real < 0)) {
        errors.push({ panelIndex, field: 'realDurationInMinutes', actionIndex: row.actionIndex, message: 'Real duration must be zero or more' })
      }
    })
  })

  return errors
}

// The documents to store, dense, under canonical keys (String(threshold)):
// - no panels -> null for the component and every operation (tiers removed);
// - operations that carry no tiers (category Other) -> null, which also clears a document left
//   over from before the operation's type changed to an Other type;
// - a real duration of zero or less is stored as null ("follow the duration"), which the
//   scheduler treats identically (ScheduledAction.cs: Real > 0 ? Real : Duration).
export function buildTierDocuments(panels, component) {
  const actions = component.actions || []

  if (!panels.length) {
    return {
      componentTiers: null,
      actionTiers: actions.map(() => null),
    }
  }

  const componentTiers = {}
  const actionTiers = actions.map(action => (isTierableAction(action) ? {} : null))

  for (const panel of panels) {
    const key = String(panel.threshold)
    componentTiers[key] = { companyCoeficient: toNumber(panel.companyCoeficient) }

    for (const row of panel.rows) {
      const document = actionTiers[row.actionIndex]
      if (!document) continue

      const real = toNumber(row.realDurationInMinutes)
      document[key] = {
        durationInMinutes: toNumber(row.durationInMinutes),
        realDurationInMinutes: real !== null && real > 0 ? real : null,
      }
    }
  }

  return { componentTiers, actionTiers }
}

// Writes a buildTierDocuments result onto a component the CALLER owns. The dialog never calls
// this on its own prop: it emits the documents and the owning form applies them, so the form
// stays the entity's only writer. Fresh objects replace the old ones, so Vue sees the change
// and the component's __hash comparison detects it on save.
export function applyTierDocuments(component, documents) {
  component.quantityTiers = documents.componentTiers
  documents.actionTiers.forEach((tiers, actionIndex) => {
    component.actions[actionIndex].quantityTiers = tiers
  })
}

// Children's work price per piece of the parent - mirrors getChildrenPriceRecursively in the
// "__priceOfWork" totals formula (MetadataRepository.GetTotalsMetadata).
function childrenWorkPrice(children) {
  if (!children || children.length === 0) return 0

  let result = 0
  children.forEach(child => {
    if (child.quantity) {
      result += child.quantity * childrenWorkPrice(child.children)
      if (child.priceOfWork) result += child.priceOfWork * child.quantity
    }
  })
  return result
}

// An operation's price per piece - the actions table's "totalPrice" formula verbatim,
// (Math.round(1000 * durationInMinutes * pricePerMinute) / 1000) || 0. The multiplication runs
// left to right; roundPrice(duration * price) multiplies in another order and can round the other
// way (1.5 min at 0.019 -> 0.028 instead of 0.029).
export function operationPrice(durationInMinutes, pricePerMinute) {
  return (Math.round(1000 * durationInMinutes * pricePerMinute) / 1000) || 0
}

// «Цена на труда (бр.)» - mirrors the "priceOfWork" and "__priceOfWork" totals formulas:
//   own work = round(sum over Internal operations of operation price x coefficient)
//   result   = round(own work + children's work)
// `durationOf(action, actionIndex)` supplies the duration to price, so one function serves base
// and every tier. The offer prices a tier's own work the same way (QuantityTierOffer.PriceOfWork),
// pinned by offerPriceVectors in Docs/fixtures/quantity-tiers.json.
export function workPricePerPiece(component, coefficient, durationOf) {
  let own = 0

  ;(component.actions || []).forEach((action, actionIndex) => {
    if (action.category !== 'Internal') return
    own += operationPrice(durationOf(action, actionIndex), action.pricePerMinute) * coefficient
  })

  return roundPrice(roundPrice(own) + childrenWorkPrice(component.children))
}

export function basePricePerPiece(component) {
  return workPricePerPiece(component, component.companyCoeficient, action => action.durationInMinutes)
}

export function panelPricePerPiece(panel, component) {
  const rows = new Map(panel.rows.map(row => [row.actionIndex, row]))

  return workPricePerPiece(component, toNumber(panel.companyCoeficient) ?? 0, (action, actionIndex) => {
    const row = rows.get(actionIndex)
    return row ? toNumber(row.durationInMinutes) ?? 0 : action.durationInMinutes
  })
}
